/**
 * Shared utility functions used across multiple services
 * Prevents code duplication and provides centralized implementations
 *
 * AUTH MODEL: httpOnly cookies only. No localStorage token storage.
 * The browser sends cookies automatically with credentials: 'include'.
 * Use checkAuthStatus() to verify auth instead of reading tokens directly.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.triagegenius.com';

/**
 * Check authentication status via httpOnly cookie
 * Replaces the old getAuthToken/isAuthenticated/isTokenExpired pattern.
 * @returns {Promise<{authenticated: boolean, user: Object|null}>}
 */
export async function checkAuthStatus() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/check`, {
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      return { authenticated: true, user: data.user };
    }
    return { authenticated: false, user: null };
  } catch (error) {
    return { authenticated: false, user: null };
  }
}

/**
 * Test Zendesk connection via OAuth
 * Shared implementation for validating Zendesk API access
 * @param {Object} zendeskConfig - Zendesk configuration object
 * @param {boolean} [zendeskConfig.useOAuth] - Use OAuth authentication (required)
 * @returns {Promise<Object>} Connection test result
 */
export async function testZendeskConnection(zendeskConfig = { useOAuth: true }) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/zendesk/test-connection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(zendeskConfig),
      credentials: 'include'
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to test Zendesk connection');
    }

    return data;
  } catch (error) {
    console.error('Zendesk connection test error:', error);
    throw error;
  }
}

/**
 * Clear authentication data
 * Calls server-side logout to clear httpOnly cookie, then clears non-auth localStorage.
 */
export async function clearAuthData() {
  try {
    await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (error) {
    // Logout is best-effort
  }
  localStorage.removeItem('onboarding_completed');
  localStorage.removeItem('user_data');
  localStorage.removeItem('quickstart_data');
  localStorage.removeItem('quickstart_partial');
}

/**
 * Create standardized API request headers
 * No Authorization header needed - httpOnly cookies are sent automatically.
 * @returns {Object} Headers object with content type
 */
export function createApiHeaders() {
  return {
    'Content-Type': 'application/json'
  };
}

/**
 * Handle API response errors consistently
 * @param {Response} response - Fetch response object
 * @param {Object} data - Parsed response data
 * @throws {Error} Throws error with appropriate message
 */
export function handleApiError(response, data) {
  if (!response.ok || !data.success) {
    const errorMessage = data?.error || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(errorMessage);
  }
}
